import { splitTsvIntoGrid } from '../table-clipboard';

/**
 * PSTUDIO-3968 (round 3) — which grid should a cell-range paste use?
 *
 * The `command+v` branch for "an in-app cell clipboard exists AND cells are
 * selected" used to cancel the keydown and replay `cellClipboard` unconditionally.
 * Cancelling suppresses the native `paste` event, which is the only permission-free
 * view of the system clipboard, so once the user had copied a cell in-app EVERY
 * later paste replayed that stale value — copying a URL in another tab changed
 * nothing until the user pressed Escape (which only "worked" because Escape calls
 * `clearCellClipboard()` and thereby drops the branch's own precondition).
 *
 * The branch now hands over to the paste event, and this resolver decides the
 * source. It leans on an existing invariant: an in-app cell copy MIRRORS itself
 * onto the system clipboard as TSV, so for our own copy the event's `text/plain`
 * equals `gridToTsv(cellClipboard)`. A mismatch therefore means the system
 * clipboard has moved on and it holds the last copy — guard rail 8's
 * "last copy wins, system-first", now honoured for cell ranges too.
 *
 * Preferring memory on a match is not just an optimisation: the in-memory grid is
 * full-fidelity (flat TSV is lossy for values containing tabs/newlines) and it is
 * what carries CUT semantics, so a same-tab cut+paste still MOVES the range.
 */
export type CellRangePasteSource =
  | { kind: 'memory' }
  | { kind: 'event'; grid: string[][] }
  // The clipboard cannot be resolved to anything writable — an image/file copy
  // with no text/plain, or a blank payload. The caller must write NOTHING (a
  // stale replay or a default-wipe is silent data loss), symmetric to the
  // single-cell resolver's `unavailable` (PSTUDIO-3968 review #1/#7).
  | { kind: 'unavailable' };

export type ResolveCellRangePasteInput = {
  /** `text/plain` from the native ClipboardEvent (permission-free). */
  eventText?: string | null;
  /** `gridToTsv(cellClipboard)` — what our own copy mirrored to the system. */
  inMemoryTsv: string;
  /**
   * The OS clipboard cannot be compared against: the copy's own system WRITE
   * failed, or that write is still in flight (a fast cut→paste would otherwise
   * see the PREVIOUS clipboard content and stop being a move).
   */
  systemWriteFailed?: boolean;
};

/**
 * Line-ending normalisation for the "is this our own copy?" comparison.
 *
 * `gridToTsv` joins rows with `\n`, but a clipboard round trip does not preserve
 * that: Chromium normalises to CRLF when writing to the OS clipboard on Windows,
 * so our own mirror comes BACK as `\r\n`. A raw byte comparison would then call
 * our own copy "external", which is not a cosmetic mismatch — it turns a same-tab
 * CUT into a non-move (source never cleared, values duplicated, guard rail 8
 * broken) and re-derives the grid through the LOSSY flat TSV instead of the
 * full-fidelity in-memory one. Neither CI platform can catch it.
 */
const normalizeNewlines = (value: string): string =>
  value.replace(/\r\n?/g, '\n');

/**
 * Pad a parsed TSV grid to a rectangle.
 *
 * `splitTsvIntoGrid` preserves whatever each logical row actually had, so an
 * external payload is often RAGGED (`"a\nb\tc"` → `[['a'], ['b','c']]`). Every
 * consumer downstream assumes a rectangle: `computeCellFillPlan` reads its width
 * from `grid[0].length` and then indexes `grid[r % rows][c % cols]`, so a ragged
 * grid either tiles column 0 over the other columns (wrong value pasted) or yields
 * `undefined`, which `convertTextToColumnValue` turns into the column DEFAULT —
 * i.e. it silently WIPES those cells. Padding with '' keeps the shape honest: a
 * short row means "these cells are blank", which is what the source showed.
 */
const toRectangle = (grid: string[][]): string[][] => {
  const width = grid.reduce((max, row) => Math.max(max, row.length), 0);
  return grid.map((row) =>
    row.length === width
      ? row
      : [...row, ...new Array<string>(width - row.length).fill('')]
  );
};

export function resolveCellRangePasteSource({
  eventText,
  inMemoryTsv,
  systemWriteFailed,
}: ResolveCellRangePasteInput): CellRangePasteSource {
  // Guard rail 7: our copy never reached the OS clipboard (write failed / still in
  // flight), so it cannot be compared against — the in-app buffer is the only
  // honest source, whatever (if anything) the event carries.
  if (systemWriteFailed === true) return { kind: 'memory' };

  const eventEmpty = typeof eventText !== 'string' || eventText.length === 0;

  // The event delivered no text/plain — an image/file copy, or a clipboard the
  // browser exposes no text for. This is NOT our copy talking (a non-empty range
  // mirrors a non-empty TSV), so replaying the in-app buffer would resurrect the
  // stale value — the exact bug this ticket fixes (review #1). The one exception:
  // a copied BLANK cell mirrors '' and legitimately matches an empty event.
  if (eventEmpty) {
    return inMemoryTsv.length === 0
      ? { kind: 'memory' }
      : { kind: 'unavailable' };
  }

  // Identical to our mirror (modulo the clipboard's own CRLF normalisation) →
  // this IS our copy. Replay it from memory.
  if (normalizeNewlines(eventText) === normalizeNewlines(inMemoryTsv)) {
    return { kind: 'memory' };
  }

  const grid = toRectangle(splitTsvIntoGrid(eventText));

  // A blank external payload (`'\n'`, `'\t\n\t'`, …) parses to an all-empty grid.
  // Pasting it would tile '' across the selection and write the column DEFAULT —
  // a silent wipe (review #7). Nothing writable here.
  if (grid.every((row) => row.every((cell) => cell.length === 0))) {
    return { kind: 'unavailable' };
  }

  return { kind: 'event', grid };
}
